import { computePosition, flip, offset, shift } from '@floating-ui/react-dom';
import {
	type PropsWithChildren,
	type RefObject,
	useEffect,
	useRef,
} from 'react';

import type { Ref } from '~types/Ref';

import { PopoverWrapper } from './Popover.styles';

export const Popover = ({
	children,
	className,
	ref,
	onShow,
}: PropsWithChildren<{ className?: string; onShow?(): void }> &
	Ref<HTMLElement>) => {
	const popoverRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const target = (ref as RefObject<HTMLElement>)?.current;
		const popover = popoverRef.current;

		if (!target || !popover) {
			return;
		}

		if (!popover.showPopover) {
			target.style.display = 'none';
			popover.style.display = 'none';
			return;
		}

		const handleMouseEnter = () => {
			if (popover) {
				onShow?.();
				popover.showPopover();
				void computePosition(target, popover, {
					placement: 'top',
					middleware: [flip(), shift({ padding: 5 }), offset(6)],
				}).then(({ x, y }) => {
					Object.assign(popover.style, {
						left: `${x}px`,
						top: `${y}px`,
					});
				});
			}
		};
		const handleMouseLeave = () => {
			if (popover) {
				popover.hidePopover();
			}
		};

		target.addEventListener('mouseenter', handleMouseEnter);
		target.addEventListener('mouseleave', handleMouseLeave);

		return () => {
			target.removeEventListener('mouseenter', handleMouseEnter);
			target.removeEventListener('mouseleave', handleMouseLeave);
		};
	}, [ref, onShow]);

	return (
		<div
			ref={popoverRef}
			className={`${PopoverWrapper} ${className ? className : ''}`}
			popover="manual"
			role="tooltip"
		>
			{children}
		</div>
	);
};
